import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Loader2, ArrowLeft, User, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const floatingWords = [
  { text: "اقرأ", top: "12%", left: "8%", size: "text-5xl md:text-7xl" },
  { text: "بِسْمِ اللَّهِ", top: "22%", right: "10%", size: "text-3xl md:text-5xl" },
  { text: "تَدَبُّر", bottom: "20%", left: "12%", size: "text-4xl md:text-6xl" },
  { text: "حِفْظ", bottom: "28%", right: "8%", size: "text-5xl md:text-7xl" },
];

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // If already logged in, redirect
  useEffect(() => {
    if (user && user.role) {
      const dest = user.role === "admin" ? "/admin" : user.role === "pembimbing" ? "/pembimbing" : "/santri";
      navigate(dest, { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setErr("");
    setLoading(true);
    try {
      const u = await login(username.trim(), password);
      toast.success(`Selamat datang, ${u.name}`);
      const dest = u.role === "admin" ? "/admin" : u.role === "pembimbing" ? "/pembimbing" : "/santri";
      navigate(location.state?.from && location.state.from !== "/login" ? location.state.from : dest, { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gagal login. Silakan coba lagi.";
      setErr(typeof msg === "string" ? msg : "Gagal login.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative min-h-screen w-full overflow-hidden bg-paper text-forest flex items-center justify-center px-6 py-24">
      {/* Floating arabic ornaments */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1550867571-6d0bc79eb6f1?crop=entropy&cs=srgb&fm=jpg&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "multiply",
          }}
        />
        <img
          src="https://customer-assets.emergentagent.com/job_generasi-flow/artifacts/fgw1jyfc_LOGO%20PONDOK%20GRQ%202022_alter.png"
          alt=""
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] md:w-[680px] lg:w-[820px] max-w-none opacity-[0.05] pointer-events-none select-none"
        />
        {floatingWords.map((w, i) => (
          <motion.span
            key={i}
            animate={{ y: [0, -18, 0] }}
            transition={{ duration: 6 + i, repeat: Infinity, ease: "easeInOut" }}
            className={`absolute hidden md:block font-arabic text-forest/[0.14] ${w.size}`}
            style={{ top: w.top, bottom: w.bottom, left: w.left, right: w.right }}
          >
            {w.text}
          </motion.span>
        ))}
      </div>

      {/* Back to landing */}
      <Link
        to="/"
        className="absolute top-6 left-6 md:top-8 md:left-8 z-10 inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-moss hover:text-gold transition-colors"
        data-testid="login-back-home"
      >
        <ArrowLeft size={14} /> Kembali
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Card */}
        <div className="bg-white/70 backdrop-blur-md border border-gold/30 rounded-2xl p-8 md:p-10 shadow-xl">
          <div className="flex flex-col items-center text-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_generasi-flow/artifacts/fgw1jyfc_LOGO%20PONDOK%20GRQ%202022_alter.png"
              alt="Logo GRQ"
              className="h-16 w-auto object-contain mb-4"
            />
            <span className="text-xs tracking-[0.4em] uppercase text-gold">Masuk Portal</span>
            <h1 className="mt-3 font-serif-display text-forest text-3xl md:text-4xl leading-tight">
              Selamat datang<br />
              <span className="italic text-gold">kembali.</span>
            </h1>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-moss mb-2">
                Username
              </label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gold" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                  className="w-full pl-10 pr-4 py-3 bg-parchment border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest"
                  placeholder="masukkan username"
                  data-testid="login-username"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-moss mb-2">
                Password
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gold" />
                <input
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="w-full pl-10 pr-11 py-3 bg-parchment border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest"
                  placeholder="masukkan password"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-moss hover:text-gold transition-colors"
                  aria-label={show ? "Sembunyikan password" : "Tampilkan password"}
                  data-testid="login-toggle-password"
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {err && (
              <p
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                data-testid="login-error"
              >
                {err}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-forest text-parchment text-sm tracking-wide hover:bg-gold transition-colors duration-500 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Memproses…
                </>
              ) : (
                "Masuk"
              )}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-gold/20">
            <p className="text-[10px] tracking-[0.25em] uppercase text-moss text-center mb-3">
              Catatan :
            </p>
            <p className="text-xs text-moss/90 text-center leading-relaxed px-2">
              Silahkan masukkan Username dan Password yang telah diberikan oleh
              Admin Pondok Tahfizh Generasi Rabbani Qurani.
            </p>
          </div>
        </div>
      </motion.div>
    </main>
  );
}
